import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router";
import {
  ArrowUpDown,
  Check,
  ChevronDown,
  Filter,
  LayoutGrid,
  Package,
  Plus,
  Search,
  Table2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useTranslation } from "@/i18n/use-translation";
import { formatCurrency } from "@/lib/format";
import { useAdminOrgStore } from "@/stores/admin-org";
import { useClaims } from "@/hooks/use-claims";
import { useResolvedConfig } from "@/hooks/use-resolved-config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { PaginationBar } from "@/components/pagination-bar";
import {
  ItemForm,
  itemFormToBody,
  type ItemFormData,
  type FoodCategory,
  type StorageType,
} from "@/components/inventory-item-form";
import { itemDisplayName } from "@/lib/inventory";
import { foodLabel } from "@/lib/food-labels";
import { allergenLabel, EU_14_ALLERGENS } from "@/lib/allergens";
import { storageTypeLabel } from "@/lib/storage-types";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

// ── Types ──────────────────────────────────────────────────────────────

interface InventoryList {
  id: string;
  name: string;
  type: string;
  list_code: string;
  item_count: number;
}

interface ResolvedInventory {
  list: InventoryList;
  resolution: string;
}

interface MenuSiteInfo {
  site_id: string;
  site_name: string;
  is_active: boolean;
}

interface MenuOption {
  id: string;
  name: string;
  sites: MenuSiteInfo[];
  is_platform_default?: boolean;
}

interface InventoryItem {
  id: string;
  list_id: string;
  description: string;
  clean_name: string | null;
  simple_name: string | null;
  sku: string | null;
  category_id: string | null;
  subcategory_id: string | null;
  storage_type_id: string | null;
  unit_pack_size: string | null;
  price: number | null;
  currency: string;
  price_per_kg: number | null;
  typical_shelf_life_days: number | null;
  typical_weight_kg: number | null;
  edible_yield_pct: number | null;
  allergens: string[];
  aliases: string[];
  supplier: string | null;
  status: string;
  ai_processed_at: string | null;
  storage_types: { name: string; display_name: string } | null;
  category: { name: string } | null;
  subcategory: { name: string } | null;
  recipe_count: number;
  readonly: boolean;
}

interface FilterState {
  storageTypes: string[];
  allergens: string[];
  categoryId: string;
  subcategoryId: string;
  shelfRange: [number, number];
}

const EMPTY_FILTERS: FilterState = {
  storageTypes: [],
  allergens: [],
  categoryId: "",
  subcategoryId: "",
  shelfRange: [0, 30],
};

// ── Main Page ──────────────────────────────────────────────────────────

export default function InventoryPage() {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const { orgId, permissions, isPlatformAdmin, siteIds } = useClaims();
  const siteId = useAdminOrgStore((s) => s.siteId);
  const setSite = useAdminOrgStore((s) => s.setSite);
  const [addOpen, setAddOpen] = useState(false);
  const [menuPopoverOpen, setMenuPopoverOpen] = useState(false);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [menuId, setMenuId] = useState("all");
  const [menuDefaultSet, setMenuDefaultSet] = useState(false);

  const navigate = useNavigate();
  const canCreate = isPlatformAdmin || permissions.includes("inventory:create");

  // Auto-select site for site managers (scoped users with no site selected)
  useEffect(() => {
    if (siteId || !orgId || isPlatformAdmin) return;
    // Empty siteIds = org-wide access (org admin) — stay in org context
    if (siteIds.length === 0) return;
    // Scoped to sites — auto-select the first one
    if (siteIds.length >= 1) {
      setSite(siteIds[0], "");
    }
  }, [siteId, orgId, isPlatformAdmin, siteIds, setSite]);

  // Resolve: site-level if site selected, org-level otherwise
  const resolvedQuery = useQuery<ResolvedInventory>({
    queryKey: ["resolved-inventory", siteId || "org", orgId],
    queryFn: async () => {
      const url = siteId
        ? `${API_URL}/api/v1/sites/${siteId}/inventory`
        : `${API_URL}/api/v1/inventory/resolve`;
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error("Failed to resolve inventory");
      return (await res.json()).data;
    },
    enabled: !!orgId,
  });

  const menusQuery = useQuery<MenuOption[]>({
    queryKey: ["menus-v2-options"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/menus/v2?page_size=100`, { headers });
      if (!res.ok) return [];
      const body = await res.json();
      return (body.data ?? []).map((m: Record<string, unknown>) => ({
        id: m.id as string,
        name: m.name as string,
        sites: (m.sites as MenuSiteInfo[]) ?? [],
      }));
    },
    enabled: !!orgId,
  });

  // POS2-647: the cross-org platform default menu. Visible to every org via
  // RLS; surfaces here so stock can be filtered to "items used by the
  // platform default's recipes".
  const platformDefaultMenuQuery = useQuery<MenuOption | null>({
    queryKey: ["platform-default-menu-option"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/default-menu`, { headers });
      if (res.status === 404) return null;
      if (!res.ok) return null;
      const body = await res.json();
      if (!body?.data) return null;
      return {
        id: body.data.id as string,
        name: body.data.name as string,
        sites: [],
        is_platform_default: true,
      };
    },
    enabled: !!orgId,
  });

  const resolved = resolvedQuery.data;
  const listId = resolved?.list.id ?? "";
  const isReadonly = resolved?.list.type !== "site_custom" && resolved?.list.type !== "org_default";
  const allMenus = useMemo(() => {
    const orgMenus = menusQuery.data ?? [];
    const pd = platformDefaultMenuQuery.data;
    return pd ? [...orgMenus, pd] : orgMenus;
  }, [menusQuery.data, platformDefaultMenuQuery.data]);

  // Filter menus to those assigned to the selected site. The platform
  // default menu has no site assignments by design — keep it visible
  // regardless of which site is selected.
  const menus = useMemo(() => {
    if (!siteId) return allMenus;
    return allMenus.filter(
      (m) => m.is_platform_default || m.sites.some((s) => s.site_id === siteId),
    );
  }, [allMenus, siteId]);

  // Auto-select active menu for the selected site
  useEffect(() => {
    if (menuDefaultSet || !menusQuery.isSuccess) return;
    if (siteId) {
      const active = menus.find((m) => m.sites.some((s) => s.site_id === siteId && s.is_active));
      setMenuId(active?.id ?? (menus.length > 0 ? menus[0].id : "all"));
    } else if (menus.length > 0) {
      setMenuId(menus[0].id);
    }
    setMenuDefaultSet(true);
  }, [menuDefaultSet, menusQuery.isSuccess, menus, siteId]);

  // Reset menu selection when site changes
  useEffect(() => {
    if (!menusQuery.isSuccess) return;
    const active = menus.find((m) => m.sites.some((s) => s.site_id === siteId && s.is_active));
    setMenuId(active?.id ?? (menus.length > 0 ? menus[0].id : "all"));
    setMenuDefaultSet(true);
  }, [siteId]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeFilterCount = [
    filters.storageTypes.length > 0,
    filters.allergens.length > 0,
    !!filters.categoryId,
    filters.shelfRange[0] > 0 || filters.shelfRange[1] < 30,
  ].filter(Boolean).length;

  if (!orgId) {
    return (
      <div className="flex flex-col items-center gap-2 py-16 text-gray-400">
        <Package className="size-10" />
        <p className="text-sm">{t("stock.noOrganisation")}</p>
      </div>
    );
  }

  if (resolvedQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="absolute inset-0 flex flex-col bg-gray-50/80 p-6">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-lg font-medium text-gray-900">{t("nav.stock")}</h1>
        <div className="flex items-center gap-2">
          <FilterPopover
            filters={filters}
            onChange={(f) => setFilters(f)}
            activeCount={activeFilterCount}
          />
          <Popover open={menuPopoverOpen} onOpenChange={setMenuPopoverOpen}>
            <PopoverTrigger asChild>
              <Button size="sm" variant="outline">
                <ChevronDown className="mr-1.5 size-4" />
                {menuId === "all"
                  ? t("stock.allMenus")
                  : (menus.find((c) => c.id === menuId)?.name ?? t("stock.allMenus"))}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-56 p-1"
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <button
                type="button"
                className={`flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm ${menuId === "all" ? "bg-gray-100 font-medium text-gray-900" : "text-gray-700 hover:bg-gray-50"}`}
                onClick={() => {
                  setMenuId("all");
                  setMenuPopoverOpen(false);
                }}
              >
                {t("stock.allItems")}
                {menuId === "all" && <Check className="size-3.5 text-gray-400" />}
              </button>
              {menus.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm ${menuId === c.id ? "bg-gray-100 font-medium text-gray-900" : "text-gray-700 hover:bg-gray-50"}`}
                  onClick={() => {
                    setMenuId(c.id);
                    setMenuPopoverOpen(false);
                  }}
                >
                  <span className="flex items-center gap-1.5">{c.name}</span>
                  {menuId === c.id && <Check className="size-3.5 text-gray-400" />}
                </button>
              ))}
              <div className="my-1 border-t border-gray-100" />
              <button
                type="button"
                className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                onClick={() => {
                  setMenuPopoverOpen(false);
                  navigate("/menus");
                }}
              >
                <Plus className="size-3.5" />
                {t("stock.addNewMenu")}
              </button>
            </PopoverContent>
          </Popover>
          {canCreate && !isReadonly && (
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <Plus className="mr-1.5 size-4" />
              {t("stock.addItem")}
            </Button>
          )}
        </div>
      </div>

      {/* Alert: no menus */}
      {menusQuery.isSuccess && menus.length === 0 && (
        <div className="mb-4 flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
          <Package className="size-8 text-gray-300" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">{t("stock.noMenuUploaded")}</p>
            <p className="mt-0.5 text-xs text-gray-500">{t("stock.noMenuHint")}</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate("/menus")}>
            <Upload className="mr-1.5 size-4" />
            {t("stock.uploadMenu")}
          </Button>
        </div>
      )}

      <AddItemDialog listId={listId} open={addOpen} onOpenChange={setAddOpen} />

      {listId && (
        <ItemsView
          listId={listId}
          menuId={menuId}
          filters={filters}
          activeFilterCount={activeFilterCount}
          isReadonly={isReadonly}
        />
      )}
    </div>
  );
}

// ── Add Item Dialog (uses shared ItemForm) ─────────────────────────────

function AddItemDialog({
  listId,
  open,
  onOpenChange,
}: {
  listId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();

  const { categories, subcategoriesFor, storageTypes } = useReferenceData();

  const createMutation = useMutation({
    mutationFn: async ({
      data,
      allergens,
      aliases,
    }: {
      data: ItemFormData;
      allergens: string[];
      aliases: string[];
    }) => {
      const res = await fetch(`${API_URL}/api/v1/inventory-lists/${listId}/items`, {
        method: "POST",
        headers,
        body: JSON.stringify(itemFormToBody(data, allergens, aliases)),
      });
      if (!res.ok) throw new Error("Failed to add item");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventory-items", listId] });
      queryClient.invalidateQueries({ queryKey: ["inventory-lists"] });
      toast.success(t("stock.itemAdded"));
      onOpenChange(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("stock.addItem")}</DialogTitle>
        </DialogHeader>
        <ItemForm
          categories={categories}
          subcategoriesFor={subcategoriesFor}
          storageTypes={storageTypes}
          onSubmit={(data, allergens, aliases) =>
            createMutation.mutate({ data, allergens, aliases })
          }
          isPending={createMutation.isPending}
          submitLabel={t("stock.addItem")}
          listId={listId}
        />
      </DialogContent>
    </Dialog>
  );
}

// ── Filter Popover ─────────────────────────────────────────────────────

function PillToggle({
  options,
  selected,
  onToggle,
}: {
  options: { id: string; label: string }[];
  selected: string[];
  onToggle: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${selected.includes(o.id) ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
          onClick={() => onToggle(o.id)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function SearchSelect({
  options,
  value,
  onChange,
  placeholder,
}: {
  options: { id: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.id === value);
  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase())).slice(0, 8)
    : options.slice(0, 8);

  if (selected) {
    return (
      <div className="flex items-center gap-1.5">
        <Badge variant="secondary" className="gap-1 rounded-full px-2 py-0.5 text-xs">
          {selected.label}
          <button
            type="button"
            onClick={() => onChange("")}
            className="text-gray-400 hover:text-red-500"
          >
            ×
          </button>
        </Badge>
      </div>
    );
  }

  return (
    <div className="relative">
      <Input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && filtered.length > 0) {
            e.preventDefault();
            onChange(filtered[0].id);
            setQuery("");
            setOpen(false);
          }
        }}
        placeholder={placeholder}
        className="h-8 text-xs"
      />
      {open && filtered.length > 0 && (
        <div className="absolute inset-x-0 top-9 z-50 max-h-40 overflow-y-auto rounded-md border border-gray-200 bg-white p-1 shadow-md">
          {filtered.map((o) => (
            <button
              key={o.id}
              type="button"
              className="block w-full rounded px-2 py-1 text-left text-xs text-gray-700 hover:bg-gray-100"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(o.id);
                setQuery("");
                setOpen(false);
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FilterPopover({
  filters,
  onChange,
  activeCount,
}: {
  filters: FilterState;
  onChange: (f: FilterState) => void;
  activeCount: number;
}) {
  const { t } = useTranslation();
  const headers = useApiHeaders();

  const storageQuery = useQuery<StorageType[]>({
    queryKey: ["storage-types"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/storage-types`, { headers });
      return (await res.json()).data;
    },
  });

  const catQuery = useQuery<FoodCategory[]>({
    queryKey: ["food-categories"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/food-categories`, { headers });
      return (await res.json()).data;
    },
  });

  const topCats = (catQuery.data ?? []).filter((c) => !c.parent_id);
  const subCats = filters.categoryId
    ? (catQuery.data ?? []).filter((c) => c.parent_id === filters.categoryId)
    : (catQuery.data ?? []).filter((c) => c.parent_id);

  function toggle(arr: string[], id: string) {
    return arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];
  }

  return (
    <Popover modal={false}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" className="relative size-8">
          <Filter className="size-4" />
          {activeCount > 0 && (
            <span className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-gray-900 text-[10px] text-white">
              {activeCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-4 p-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-gray-900">{t("stock.filters")}</p>
          {activeCount > 0 && (
            <button
              type="button"
              className="text-xs text-gray-400 hover:text-gray-600"
              onClick={() => onChange(EMPTY_FILTERS)}
            >
              {t("stock.clearAll")}
            </button>
          )}
        </div>
        <div>
          <p className="mb-1.5 text-xs text-gray-400">{t("stock.storageType")}</p>
          <PillToggle
            options={(storageQuery.data ?? []).map((st) => ({
              id: st.id,
              label: storageTypeLabel(t, st) ?? st.display_name,
            }))}
            selected={filters.storageTypes}
            onToggle={(id) =>
              onChange({ ...filters, storageTypes: toggle(filters.storageTypes, id) })
            }
          />
        </div>
        <div>
          <p className="mb-1.5 text-xs text-gray-400">{t("stock.allergens")}</p>
          <PillToggle
            options={EU_14_ALLERGENS.map((a) => ({ id: a, label: allergenLabel(t, a) }))}
            selected={filters.allergens}
            onToggle={(id) => onChange({ ...filters, allergens: toggle(filters.allergens, id) })}
          />
        </div>
        <div>
          <p className="mb-1 text-xs text-gray-400">{t("stock.category")}</p>
          <SearchSelect
            options={topCats.map((c) => ({ id: c.id, label: foodLabel(t, c.name) }))}
            value={filters.categoryId}
            onChange={(v) => onChange({ ...filters, categoryId: v, subcategoryId: "" })}
            placeholder={t("stock.searchCategories")}
          />
        </div>
        <div>
          <p className="mb-1 text-xs text-gray-400">{t("stock.subcategory")}</p>
          <SearchSelect
            options={subCats.map((c) => ({ id: c.id, label: foodLabel(t, c.name) }))}
            value={filters.subcategoryId}
            onChange={(v) => onChange({ ...filters, subcategoryId: v })}
            placeholder={t("stock.searchSubcategories")}
          />
        </div>
        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs text-gray-400">{t("stock.shelfLifeShort")}</p>
            <p className="text-xs tabular-nums text-gray-500">
              {t("stock.shelfRange", {
                from: filters.shelfRange[0],
                to:
                  filters.shelfRange[1] >= 30
                    ? t("stock.shelfMax")
                    : t("stock.shelfDays", { days: filters.shelfRange[1] }),
              })}
            </p>
          </div>
          <Slider
            min={0}
            max={30}
            step={1}
            value={filters.shelfRange}
            onValueChange={(v) => onChange({ ...filters, shelfRange: v as [number, number] })}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ── Items View ─────────────────────────────────────────────────────────

function ItemsView({
  listId,
  menuId,
  filters,
  activeFilterCount,
  isReadonly,
}: {
  listId: string;
  menuId: string;
  filters: FilterState;
  activeFilterCount: number;
  isReadonly: boolean;
}) {
  const { locale, t } = useTranslation();
  // Item prices follow the org→site→kitchen config stack, resolving at the selected site and
  // falling back to the org — Stock is site-scoped when a site is chosen. Both figures here were
  // wrong for a non-euro organisation: a literal `€` on the price and a hardcoded `"EUR"` code on
  // the per-kg line, so one read `€4.00` and the other `4,00 €/kg` on the same row (POS2-1134).
  const { orgId: claimsOrgId, isPlatformAdmin: isPlatformAdminUser } = useClaims();
  const adminOrgId = useAdminOrgStore((st) => st.orgId);
  const adminSiteId = useAdminOrgStore((st) => st.siteId);
  const configOrgId = isPlatformAdminUser ? adminOrgId : claimsOrgId;
  const resolvedCurrency = useResolvedConfig(
    adminSiteId ? "site" : "organisation",
    adminSiteId || configOrgId || null,
  ).data?.config.currency?.value;
  const currency = typeof resolvedCurrency === "string" ? resolvedCurrency : "GBP";
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [view, setView] = useState<"table" | "cards">("table");
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [sortBy, setSortBy] = useState("recipe_count");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  function toggleSort(field: string) {
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
    setPage(0);
  }

  const itemsQuery = useQuery<InventoryItem[]>({
    queryKey: ["inventory-items", listId, menuId, search, page, pageSize, filters, sortBy, sortDir],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: String(pageSize),
        offset: String(page * pageSize),
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      if (menuId && menuId !== "all") params.set("menu_id", menuId);
      if (search) params.set("search", search);
      for (const id of filters.storageTypes) params.append("storage_type_id", id);
      for (const a of filters.allergens) params.append("allergen", a);
      if (filters.categoryId) params.append("category_id", filters.categoryId);
      if (filters.subcategoryId) params.append("subcategory_id", filters.subcategoryId);
      if (filters.shelfRange[0] > 0) params.set("shelf_life_min", String(filters.shelfRange[0]));
      if (filters.shelfRange[1] < 30) params.set("shelf_life_max", String(filters.shelfRange[1]));
      const res = await fetch(`${API_URL}/api/v1/inventory-lists/${listId}/items?${params}`, {
        headers,
      });
      if (!res.ok) throw new Error("Failed to load items");
      const count = res.headers.get("X-Total-Count");
      if (count) setTotalCount(Number(count));
      return (await res.json()).data;
    },
    enabled: !!listId,
  });

  const items = itemsQuery.data ?? [];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-3 pb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder={t("stock.searchItems")}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            className="pl-9"
          />
        </div>
        <div className="flex items-center rounded-md border border-gray-200">
          <Button
            size="sm"
            variant={view === "table" ? "secondary" : "ghost"}
            className="h-8 rounded-r-none px-2.5"
            onClick={() => {
              setView("table");
              setPage(0);
            }}
          >
            <Table2 className="size-4" />
          </Button>
          <Button
            size="sm"
            variant={view === "cards" ? "secondary" : "ghost"}
            className="h-8 rounded-l-none px-2.5"
            onClick={() => {
              setView("cards");
              setPage(0);
            }}
          >
            <LayoutGrid className="size-4" />
          </Button>
        </div>
      </div>

      {/* Edit dialog */}
      <EditItemDialog
        item={editingItem}
        isReadonly={isReadonly}
        onClose={() => setEditingItem(null)}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ["inventory-items", listId] });
          setEditingItem(null);
        }}
        onDeleted={() => {
          queryClient.invalidateQueries({ queryKey: ["inventory-items", listId] });
          queryClient.invalidateQueries({ queryKey: ["inventory-lists"] });
          setEditingItem(null);
        }}
      />

      {/* Scrollable content area */}
      <div className="min-h-0 flex-1 overflow-auto">
        {itemsQuery.isLoading && (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        )}
        {itemsQuery.isError && (
          <div className="rounded border border-red-200 bg-red-50 p-3">
            <p className="text-sm text-red-700">{t("stock.loadItemsFailed")}</p>
          </div>
        )}
        {!itemsQuery.isLoading && items.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-12 text-gray-400">
            <Package className="size-8" />
            <p className="text-sm">
              {search || activeFilterCount > 0 ? t("stock.noItemsMatch") : t("stock.noItems")}
            </p>
          </div>
        )}

        {items.length > 0 && view === "table" && (
          <div className="rounded-lg border border-black/[0.06] bg-white">
            <Table>
              <TableHeader>
                <TableRow className="border-none hover:bg-transparent">
                  <TableHead className="w-[35%] px-4 py-2 text-xs uppercase tracking-wide text-gray-400">
                    <SortHeader
                      label={t("stock.columnItem")}
                      field="description"
                      current={sortBy}
                      dir={sortDir}
                      onToggle={toggleSort}
                    />
                  </TableHead>
                  <TableHead className="px-4 py-2 text-xs uppercase tracking-wide text-gray-400">
                    <SortHeader
                      label={t("stock.columnSupplier")}
                      field="supplier"
                      current={sortBy}
                      dir={sortDir}
                      onToggle={toggleSort}
                    />
                  </TableHead>
                  <TableHead className="px-4 py-2 text-xs uppercase tracking-wide text-gray-400">
                    {t("stock.columnCategory")}
                  </TableHead>
                  <TableHead className="px-4 py-2 text-right text-xs uppercase tracking-wide text-gray-400">
                    <SortHeader
                      label={t("stock.columnRecipes")}
                      field="recipe_count"
                      current={sortBy}
                      dir={sortDir}
                      onToggle={toggleSort}
                      align="right"
                    />
                  </TableHead>
                  <TableHead className="px-4 py-2 text-right text-xs uppercase tracking-wide text-gray-400">
                    <SortHeader
                      label={t("stock.columnYield")}
                      field="edible_yield_pct"
                      current={sortBy}
                      dir={sortDir}
                      onToggle={toggleSort}
                      align="right"
                    />
                  </TableHead>
                  <TableHead className="px-4 py-2 text-right text-xs uppercase tracking-wide text-gray-400">
                    <SortHeader
                      label={t("stock.columnPrice")}
                      field="price"
                      current={sortBy}
                      dir={sortDir}
                      onToggle={toggleSort}
                      align="right"
                    />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow
                    key={item.id}
                    className="cursor-pointer border-black/[0.06] transition-all duration-[120ms] ease-out hover:border-l-2 hover:border-l-blue-500 hover:bg-black/[0.02] [&:first-child]:border-t-0 [&:last-child]:border-b-0"
                    onClick={() => setEditingItem(item)}
                  >
                    <TableCell className="h-11 px-4 py-0">
                      <div className="flex items-center gap-2">
                        {item.status !== "active" && (
                          <span
                            className={`size-1.5 shrink-0 rounded-full ${item.status === "pending_review" || item.status === "seasonal" ? "bg-orange-500" : "bg-red-500"}`}
                            title={item.status}
                          />
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-medium leading-tight text-gray-900">
                            {itemDisplayName(item)}
                            {item.allergens.length > 0 &&
                              item.allergens.map((a) => (
                                <span
                                  key={a}
                                  className="ml-1 rounded-full bg-orange-50 px-1.5 py-0 text-[10px] font-medium text-orange-700"
                                >
                                  {allergenLabel(t, a)}
                                </span>
                              ))}
                          </p>
                          <p className="text-xs text-gray-400">
                            {[item.unit_pack_size, storageTypeLabel(t, item.storage_types)]
                              .filter(Boolean)
                              .join(" · ") || ""}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="h-11 px-4 py-0 text-sm text-gray-500">
                      {item.supplier || "—"}
                    </TableCell>
                    <TableCell className="h-11 px-4 py-0">
                      <span className="text-xs text-gray-400">
                        {foodLabel(t, item.subcategory?.name) ??
                          foodLabel(t, item.category?.name) ??
                          "—"}
                      </span>
                    </TableCell>
                    <TableCell className="h-11 px-4 py-0 text-right">
                      <span
                        className={`text-sm tabular-nums ${item.recipe_count > 0 ? "font-medium text-gray-900" : "text-gray-300"}`}
                      >
                        {item.recipe_count || "—"}
                      </span>
                    </TableCell>
                    <TableCell className="h-11 px-4 py-0 text-right">
                      {item.edible_yield_pct != null ? (
                        <span
                          className={`text-xs tabular-nums ${item.edible_yield_pct >= 90 ? "text-green-700" : item.edible_yield_pct >= 70 ? "text-amber-600" : "text-red-600"}`}
                        >
                          {item.edible_yield_pct}%
                        </span>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </TableCell>
                    <TableCell className="h-11 px-4 py-0 text-right">
                      <p className="text-sm font-medium tabular-nums text-gray-900">
                        {item.price != null ? formatCurrency(locale, item.price, currency, 2) : "—"}
                      </p>
                      {item.price_per_kg != null && (
                        <p className="text-xs tabular-nums text-gray-400">
                          {t("stock.pricePerKgValue", {
                            value: formatCurrency(locale, item.price_per_kg, currency),
                          })}
                        </p>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {items.length > 0 && view === "cards" && (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-4">
            {items.map((item) => (
              <button
                key={item.id}
                className="rounded-lg border border-black/[0.06] bg-white p-4 text-left transition-colors hover:bg-black/[0.02]"
                onClick={() => setEditingItem(item)}
              >
                <p className="text-sm font-medium leading-tight text-gray-900">
                  {itemDisplayName(item)}
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  {[item.unit_pack_size, storageTypeLabel(t, item.storage_types)]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </p>
                {item.price != null && (
                  <p className="mt-2 text-sm font-medium tabular-nums text-gray-900">
                    {formatCurrency(locale, item.price, currency, 2)}
                    {item.price_per_kg != null && (
                      <span className="ml-1 text-xs font-normal text-gray-400">
                        {t("stock.pricePerKgValue", {
                          value: formatCurrency(locale, item.price_per_kg, currency),
                        })}
                      </span>
                    )}
                  </p>
                )}
                {item.allergens.length > 0 && (
                  <p className="mt-2 text-[10px] text-gray-400">{item.allergens.join(", ")}</p>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Pagination — always visible at bottom */}
      {items.length > 0 && (
        <PaginationBar
          page={page}
          pageSize={pageSize}
          total={totalCount}
          onPageChange={setPage}
          onPageSizeChange={(s) => {
            setPageSize(s);
            setPage(0);
          }}
        />
      )}
    </div>
  );
}

// ── Connected Recipes ─────────────────────────────────────────────────

function ConnectedRecipes({
  recipes,
}: {
  recipes: { id: string; name: string; status: string }[];
}) {
  const { t } = useTranslation();
  if (recipes.length === 0) return null;
  return (
    <div className="border-t border-gray-100 pt-3">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-gray-400">
        {t("stock.usedInRecipes")}
        <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] tabular-nums text-gray-500">
          {recipes.length}
        </span>
      </p>
      <div className="flex flex-col gap-0.5">
        {recipes.map((r) => (
          <Link
            key={r.id}
            to={`/menus/recipes/${r.id}`}
            className="rounded-md px-2 py-1 text-sm text-blue-600 hover:bg-gray-50 hover:text-blue-700"
          >
            {r.name}
          </Link>
        ))}
      </div>
    </div>
  );
}

// ── Edit Item Dialog (uses shared ItemForm) ────────────────────────────

function EditItemDialog({
  item,
  isReadonly,
  onClose,
  onSaved,
  onDeleted,
}: {
  item: InventoryItem | null;
  isReadonly: boolean;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const { locale, t } = useTranslation();
  // Item prices follow the org→site→kitchen config stack, resolving at the selected site and
  // falling back to the org — Stock is site-scoped when a site is chosen. Both figures here were
  // wrong for a non-euro organisation: a literal `€` on the price and a hardcoded `"EUR"` code on
  // the per-kg line, so one read `€4.00` and the other `4,00 €/kg` on the same row (POS2-1134).
  const { orgId: claimsOrgId, isPlatformAdmin: isPlatformAdminUser } = useClaims();
  const adminOrgId = useAdminOrgStore((st) => st.orgId);
  const adminSiteId = useAdminOrgStore((st) => st.siteId);
  const configOrgId = isPlatformAdminUser ? adminOrgId : claimsOrgId;
  const resolvedCurrency = useResolvedConfig(
    adminSiteId ? "site" : "organisation",
    adminSiteId || configOrgId || null,
  ).data?.config.currency?.value;
  const currency = typeof resolvedCurrency === "string" ? resolvedCurrency : "GBP";
  const headers = useApiHeaders();
  const { categories, subcategoriesFor, storageTypes } = useReferenceData();

  const recipesQuery = useQuery<{ id: string; name: string; status: string }[]>({
    queryKey: ["item-recipes", item?.id],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/inventory-items/${item!.id}/recipes`, { headers });
      if (!res.ok) return [];
      return (await res.json()).data;
    },
    enabled: !!item,
  });
  const connectedRecipes = recipesQuery.data ?? [];

  const updateMutation = useMutation({
    mutationFn: async ({
      data,
      allergens,
      aliases,
    }: {
      data: ItemFormData;
      allergens: string[];
      aliases: string[];
    }) => {
      const res = await fetch(`${API_URL}/api/v1/inventory-items/${item!.id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(itemFormToBody(data, allergens, aliases)),
      });
      if (!res.ok) throw new Error("Failed to save");
      return res.json();
    },
    onSuccess: () => {
      toast.success(t("stock.itemUpdated"));
      onSaved();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/inventory-items/${item!.id}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => {
      toast.success(t("stock.itemDeleted"));
      onDeleted();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (!item) return null;

  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isReadonly ? t("stock.viewItem") : t("stock.editItem")}</DialogTitle>
        </DialogHeader>
        {isReadonly ? (
          <div className="space-y-3 text-sm text-gray-700">
            <p className="text-base font-semibold">{itemDisplayName(item)}</p>
            <p className="text-xs text-gray-400">
              {item.sku ?? "—"} · {item.unit_pack_size ?? "—"}
            </p>
            {item.price != null && (
              <p>
                {formatCurrency(locale, item.price, currency, 2)}
                {item.price_per_kg != null && (
                  <span className="ml-1 text-xs text-gray-400">
                    {t("stock.pricePerKgValue", {
                      value: formatCurrency(locale, item.price_per_kg, currency),
                    })}
                  </span>
                )}
              </p>
            )}
            <div className="flex flex-wrap gap-1">
              {item.category && (
                <Badge variant="secondary" className="rounded-full text-xs">
                  {foodLabel(t, item.category.name)}
                </Badge>
              )}
              {item.subcategory && (
                <Badge variant="secondary" className="rounded-full text-xs">
                  {foodLabel(t, item.subcategory.name)}
                </Badge>
              )}
              {item.storage_types && (
                <Badge variant="secondary" className="rounded-full text-xs">
                  {storageTypeLabel(t, item.storage_types)}
                </Badge>
              )}
            </div>
            {item.allergens.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {item.allergens.map((a) => (
                  <Badge
                    key={a}
                    className="rounded-full bg-orange-50 px-2 py-0.5 text-xs text-orange-700"
                  >
                    {a}
                  </Badge>
                ))}
              </div>
            )}
            <ConnectedRecipes recipes={connectedRecipes} />
            <Button variant="outline" size="sm" className="w-full" onClick={onClose}>
              {t("stock.close")}
            </Button>
          </div>
        ) : (
          <>
            <ItemForm
              categories={categories}
              subcategoriesFor={subcategoriesFor}
              storageTypes={storageTypes}
              defaultValues={{
                description: item.description,
                sku: item.sku ?? "",
                category_id: item.category_id ?? "",
                subcategory_id: item.subcategory_id ?? "",
                storage_type_id: item.storage_type_id ?? "",
                unit_pack_size: item.unit_pack_size ?? "",
                price: item.price ?? undefined,
                currency: item.currency,
                price_per_kg: item.price_per_kg ?? undefined,
                typical_shelf_life_days: item.typical_shelf_life_days ?? undefined,
                typical_weight_kg: item.typical_weight_kg ?? undefined,
                edible_yield_pct: item.edible_yield_pct ?? undefined,
                supplier: item.supplier ?? "",
                status: item.status as ItemFormData["status"],
              }}
              defaultAllergens={item.allergens}
              defaultAliases={item.aliases}
              onSubmit={(data, allergens, aliases) =>
                updateMutation.mutate({ data, allergens, aliases })
              }
              isPending={updateMutation.isPending}
              submitLabel={t("stock.saveChanges")}
              listId={item.list_id}
              onDelete={() => {
                if (confirm(`Delete "${item.description}"?`)) deleteMutation.mutate();
              }}
              isDeleting={deleteMutation.isPending}
            />
            <ConnectedRecipes recipes={connectedRecipes} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Reference Data Hook ────────────────────────────────────────────────

function useReferenceData() {
  const headers = useApiHeaders();

  const catQuery = useQuery<FoodCategory[]>({
    queryKey: ["food-categories"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/food-categories`, { headers });
      return (await res.json()).data;
    },
  });

  const stQuery = useQuery<StorageType[]>({
    queryKey: ["storage-types"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/storage-types`, { headers });
      return (await res.json()).data;
    },
  });

  const categories = useMemo(
    () => (catQuery.data ?? []).filter((c) => !c.parent_id),
    [catQuery.data],
  );
  function subcategoriesFor(parentId: string) {
    return (catQuery.data ?? []).filter((c) => c.parent_id === parentId);
  }

  return { categories, subcategoriesFor, storageTypes: stQuery.data ?? [] };
}

// ── Sort Header ───────────────────────────────────────────────────────

function SortHeader({
  label,
  field,
  current,
  dir,
  onToggle,
  align,
}: {
  label: string;
  field: string;
  current: string;
  dir: "asc" | "desc";
  onToggle: (f: string) => void;
  align?: "right";
}) {
  const active = current === field;
  return (
    <span
      role="button"
      tabIndex={0}
      className={`inline-flex cursor-pointer select-none items-center gap-1 ${align === "right" ? "justify-end" : ""}`}
      onClick={() => onToggle(field)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onToggle(field);
      }}
    >
      {label}
      <ArrowUpDown
        className={`size-3 ${active ? "text-gray-900" : "text-gray-300"} ${active && dir === "desc" ? "rotate-180" : ""}`}
      />
    </span>
  );
}
